import queryString from 'query-string';
import {
  auth0Context,
  getAuthToken,
  redirectToLogout,
  redirectToV2Logout,
} from './authUtils';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const quotesApiURL = process.env.REACT_APP_API_BASE_QUOTE_URL;
const userServiceURL = process.env.REACT_APP_USER_SERVICE_URL;

const getBaseServiceURL = (serviceName?: string) => {
  switch (serviceName) {
    case 'quoteService':
      return quotesApiURL;
    case 'userService':
      return userServiceURL;
    default:
      return BASE_URL;
  }
};

const defaultOptions = async () => ({
  headers: {
    Authorization: `Bearer ${await getAuthToken()}`,
    'Content-Type': 'application/json',
  },
});

const handleResponse = async (response: Response) => {
  if (response.status === 401 || response.status === 403) {
    if (auth0Context.isAuthenticated) {
      redirectToV2Logout();
    } else {
      redirectToLogout();
    }
    return;
  }

  const json = await response.json();
  if (!response.ok) {
    throw new Error(json.message);
  }
  return json;
};

const get =
  <T, Q = {}>(url: string, serviceName?: string) =>
  async (query?: Q): Promise<T> => {
    const baseURL = getBaseServiceURL(serviceName);
    const formattedUrl = query
      ? `${baseURL}/${url}?${queryString.stringify(query as any)}`
      : `${baseURL}/${url}`;

    return handleResponse(await fetch(formattedUrl, await defaultOptions()));
  };

const getWithURL =
  <T, Q = {}>(url: string) =>
  async (query?: Q): Promise<T> => {
    const formattedUrl = query
      ? `${url}?${queryString.stringify(query as any)}`
      : `${url}`;
    return handleResponse(await fetch(formattedUrl, await defaultOptions()));
  };

const post =
  <T, B>(url: string, serviceName?: string) =>
  async (body: B): Promise<T> => {
    const baseURL = getBaseServiceURL(serviceName);
    const getDefaultOptions = await defaultOptions();
    return handleResponse(
      await fetch(`${baseURL}/${url}`, {
        ...getDefaultOptions,
        method: 'POST',
        body: JSON.stringify(body),
      })
    );
  };

const put =
  <T, B>(url: string, serviceName?: string) =>
  async (body: B): Promise<T> => {
    const baseURL = getBaseServiceURL(serviceName);
    const getDefaultOptions = await defaultOptions();
    return handleResponse(
      await fetch(`${baseURL}/${url}`, {
        ...getDefaultOptions,
        method: 'PUT',
        body: JSON.stringify(body),
      })
    );
  };

const _delete =
  <T, B>(url: string, serviceName?: string) =>
  async (body: B): Promise<T> => {
    const baseURL = getBaseServiceURL(serviceName);
    const getDefaultOptions = await defaultOptions();
    return handleResponse(
      await fetch(`${baseURL}/${url}`, {
        ...getDefaultOptions,
        method: 'DELETE',
        body: JSON.stringify(body),
      })
    );
  };

export default {
  get,
  getWithURL,
  put,
  post,
  _delete,
};
